import React, { type FC, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { SearchInput } from '../Inputs/Search/SearchInput';
import { type ExtendedDataRequest } from './interfaces';
import clsx from 'clsx';

import './MaterialTableFilterBar.scss';

interface MaterialTableFilterBarProps<T = any> {
    /**
     * Original unfiltered data from the table
     */
    data: T[] | undefined;

    /**
     * Extended data request object with filter configuration
     */
    dataRequest: ExtendedDataRequest<T>;

    /**
     * Callback invoked with updated data request whenever filters/search changes
     * Parent component should initialize a state with the original data
     * and update it when this callback is invoked
     */
    onDataRequestChange: (request: ExtendedDataRequest<T> | ((prev: ExtendedDataRequest<T>) => ExtendedDataRequest<T>)) => void;

    /**
     * Callback invoked with filtered data whenever filters/search changes
     * Parent component should initialize a state with the original data
     * and update it when this callback is invoked
     */
    onFilterData: (filteredData: T[]) => void;

    /**
     * Optional CSS class name
     */
    className?: string;

    /**
     * Label for the search input
     */
    searchLabel?: string;

    /**
     * Placeholder text for the search input
     */
    searchPlaceholder?: string;
}

/**
 * MaterialTableFilterBar
 *
 * A reusable filter bar component for MaterialTable that handles:
 * - Field-based filtering with finite options (typically < 10 values)
 * - Mid-string search across multiple specified fields
 *
 * Internal filtering logic removes boilerplate from parent components.
 * Parent component maintains a filteredData state initialized to table data,
 * which is updated via onFilterChange callback.
 *
 * @example
 * ```tsx
 * const [filteredData, setFilteredData] = useState<CIReferral[]>(referrals || []);
 * const [dataRequest, setDataRequest] = useState<ExtendedDataRequest<CIReferral>>({
 *   search: '',
 *   filterOptions: [{ value: '', label: 'All' }],
 *   filterField: 'status',
 *   filterValue: null,
 *   searchFields: ['orderId', 'company']
 * });
 *
 * return (
 *   <>
 *     <MaterialTableFilterBar<CIReferral>
 *       data={referrals}
 *       dataRequest={dataRequest}
 *       onFilterChange={setFilteredData}
 *       filterLabel="Status"
 *       searchLabel="Search"
 *       searchPlaceholder="Search by order ID or company..."
 *     />
 *     <MaterialTable data={filteredData} columns={columns} />
 *   </>
 * );
 * ```
 */
export const MaterialTableFilterBar: FC<MaterialTableFilterBarProps> = <T extends any = any>(props: MaterialTableFilterBarProps<T>) => {
    const {
        data,
        dataRequest,
        onDataRequestChange,
        onFilterData: onFilterChange,
        className,
        searchLabel = 'Search',
        searchPlaceholder = 'Search...'
    } = props;

    /**
     * Apply both field-based and search-based filters internally
     */
    const applyFilters = useCallback<(dataToFilter: T[] | undefined, request: ExtendedDataRequest<T>) => T[]>(
        (dataToFilter: T[] | undefined, request: ExtendedDataRequest<T>): T[] => {
            if (!dataToFilter) return [];

            let filtered = [...dataToFilter];

            // 1. Apply field-based filter if configured
            if (request.filterConfig?.options && request.filterConfig.options.length > 0) {
                const selectedFilterValue = request.filterConfig.value;

                if (selectedFilterValue && selectedFilterValue !== 'ALL') {
                    filtered = filtered.filter((item) => {
                        if (typeof request.filterConfig?.filterBy?.[selectedFilterValue] === 'function') {
                            return request.filterConfig?.filterBy?.[selectedFilterValue](item);
                        }
                        const itemValue = item[request.filterConfig?.field!];
                        return itemValue === selectedFilterValue;
                    });
                }
            }

            // 2. Apply mid-string search across specified fields
            if (request.search && request.searchFields && request.searchFields.length > 0) {
                const searchLower = request.search.toLowerCase();
                if (request.searchFields.length === 1 && request.searchFields[0] === 'ALL') {
                    filtered = filtered.filter((item) =>
                        Object.keys(item as Object).some((field) => {
                            const fieldValue = String(item[field] || '');
                            return fieldValue.toLowerCase().includes(searchLower);
                        })
                    );
                } else {
                    filtered = filtered.filter((item) =>
                        request.searchFields!.some((field) => {
                            const fieldValue = String(item[field] || '');
                            return fieldValue.toLowerCase().includes(searchLower);
                        })
                    );
                }
            }
            return filtered;
        },
        []
    );

    /*
     * Handle filter value change from dropdown
     */
    const handleFilterChange = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const newValue = (e.currentTarget as HTMLButtonElement)?.value as T[keyof T] | '' | null;
        onDataRequestChange((prev: ExtendedDataRequest<T>) => ({
            ...prev,
            filterConfig: {
                ...prev.filterConfig!,
                value: newValue
            }
        }));
    }, [onDataRequestChange]);

    const handleSearchChange = useCallback((newValue: string) => {
        onDataRequestChange((prev: ExtendedDataRequest<T>) => ({
            ...prev,
            search: newValue
        }));
    }, [onDataRequestChange]);

    /**
     * Memoize filtered data to avoid unnecessary re-renders
     */
    const filteredData = useMemo(() => {
        return applyFilters(data, dataRequest);
    }, [data, dataRequest, applyFilters]);

    /**
     * Notify parent of filtered data changes
     */
    useEffect(() => {
        onFilterChange(filteredData);
    }, [filteredData.length, dataRequest.filterConfig?.value, onFilterChange]);

    const hasFilterOptions = dataRequest.filterConfig?.options && dataRequest.filterConfig.options.length > 0;
    const hasSearchFields = dataRequest.searchFields && dataRequest.searchFields.length > 0;

    if (!hasFilterOptions && !hasSearchFields) {
        return null;
    }

    return (
        <Box className={clsx('material_table_filter_bar', className)} sx={{ display: 'flex', gap: 2, mb: 2 }}>
            {/* Filter Button Group */}
            {hasFilterOptions && (
                <div className="material_table_filter_toggle_button_group_wrapper">
                    <ToggleButtonGroup
                        className="material_table_filter_toggle_button_group"
                        exclusive
                        size="small"
                        value={dataRequest.filterConfig?.value}
                        onChange={handleFilterChange}
                    >
                        {dataRequest.filterConfig?.options?.map((option, index) => (
                            // @ts-ignore - TypeScript may complain about value types, but we assume parent component ensures correct types
                            <ToggleButton key={index} value={option.value}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </div>
            )}

            {/* Search Field */}
            {hasSearchFields && (
                <SearchInput
                    value={dataRequest.search || ''}
                    onChange={handleSearchChange}
                    label={searchLabel}
                    placeholder={searchPlaceholder}
                    ariaLabel="Search table"
                />
            )}
        </Box>
    );
};

export default MaterialTableFilterBar;
